// Feature: M004-F0696 (M001-D033 §8) — app-declared permission keys in the role matrix.
// Test: T-M004-0170
//
// Pure helpers. NOTHING here names an application or a key: every key, label,
// default and state comes from the `applicationPermissions` block of Core's
// `GET /v1/admin/organisations/:organisationId/permissions` response (D033 §5).
// A key the server returns is rendered; a key the server does not return is
// never invented. There is no allowlist, and no decision is made from a role
// name (the role names are only the column headings the server sent).

const text = (value) => (typeof value === 'string' ? value : '');

const normaliseCells = (rawRoles) => {
  const cells = [];
  const entries = Object.entries(rawRoles && typeof rawRoles === 'object' && !Array.isArray(rawRoles) ? rawRoles : {});
  entries.forEach(([role, cell]) => {
    if (!role || !cell || typeof cell !== 'object') return;
    const override = cell.override === true || cell.override === false ? cell.override : null;
    cells.push({
      role: String(role),
      default: cell.default === true,
      override,
      effective: typeof cell.effective === 'boolean'
        ? cell.effective
        : (override === null ? cell.default === true : override),
    });
  });
  return cells;
};

/**
 * Core's `applicationPermissions` (appPermissionCatalogue.service.ts `matrix()`):
 * [{ appCode, displayName, keys: [{ permissionName, key, label, description,
 * loginRequired, status, roles: { <role>: { default, override, effective } } }] }].
 * -> [{ appCode, appName, roles[], keys[{ name, key, label,
 * description, loginRequired, status, retired, cells{role: cell} }] }].
 * Malformed entries are skipped (fail closed: nothing is rendered editable
 * that the server did not describe).
 */
export const normaliseApplicationPermissions = (raw) => {
  const list = Array.isArray(raw) ? raw : [];
  const apps = [];
  list.forEach((app) => {
    const appCode = text(app?.appCode);
    if (!appCode) return;
    const rawKeys = Array.isArray(app.keys) ? app.keys : [];
    const keys = [];
    const roles = [];
    rawKeys.forEach((entry) => {
      const name = text(entry?.permissionName);
      if (!name) return;
      const status = text(entry.status) || 'active';
      const cellList = normaliseCells(entry.roles);
      const cells = {};
      cellList.forEach((cell) => {
        cells[cell.role] = cell;
        if (!roles.includes(cell.role)) roles.push(cell.role);
      });
      keys.push({
        name,
        key: text(entry.key) || (name.startsWith(`${appCode}.`) ? name.slice(appCode.length + 1) : name),
        label: text(entry.label) || name,
        description: text(entry.description),
        loginRequired: entry.loginRequired === true,
        status,
        retired: status === 'retired',
        cells,
      });
    });
    if (!keys.length) return;
    apps.push({ appCode, appName: text(app.displayName) || appCode, roles, keys });
  });
  return apps;
};

export const pendingKey = (name, role) => `${name}\u0000${role}`;

/** The state a cell will have once the pending change (if any) is saved. */
export const displayedCell = (cell, pending) => {
  const base = cell || { default: false, override: null, effective: false };
  if (pending && Object.prototype.hasOwnProperty.call(pending, 'value')) {
    const override = pending.value;
    return {
      default: base.default,
      override,
      effective: override === null ? base.default : override,
      changed: true,
    };
  }
  return { ...base, changed: false };
};

export const cellSource = (shown) => {
  if (shown.changed) return 'changed (not yet saved)';
  return shown.override === null ? 'inherited from registration' : 'overridden in this org';
};

/**
 * "Configure cameras without View cameras: cannot sign in to <app>" — a role
 * that holds a non-login key but not every login key of the same app.
 */
export const loginKeyWarnings = (apps, pendingChanges) => {
  const warnings = [];
  apps.forEach((app) => {
    const live = app.keys.filter((k) => !k.retired);
    const loginKeys = live.filter((k) => k.loginRequired);
    const otherKeys = live.filter((k) => !k.loginRequired);
    if (!loginKeys.length) return;
    app.roles.forEach((role) => {
      const effective = (k) => displayedCell(k.cells[role], pendingChanges[pendingKey(k.name, role)]).effective;
      const missing = loginKeys.filter((k) => !effective(k));
      if (!missing.length) return;
      otherKeys.filter(effective).forEach((k) => {
        warnings.push({
          id: `${k.name}:${role}`,
          appCode: app.appCode,
          role,
          message: `${k.label} without ${missing.map((m) => m.label).join(', ')}: cannot sign in to ${app.appName} (${role})`,
        });
      });
    });
  });
  return warnings;
};

/** pending map -> { role: { [permissionName]: true|false|null } }; merged into the PUT's `permissions` map. */
export const appPermissionsByRole = (pendingChanges) => {
  const byRole = {};
  Object.values(pendingChanges).forEach((change) => {
    if (!byRole[change.role]) byRole[change.role] = {};
    byRole[change.role][change.name] = change.value;
  });
  return byRole;
};
